import { ChangeDetectorRef, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Firestore, collection, doc, onSnapshot } from '@angular/fire/firestore';
import { Data } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { AuthGamesService } from '@core/AuthServices/authGames/auth-games.service';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, filter, map, take, takeUntil, throttleTime } from 'rxjs/operators';
import { CommonVariable } from 'src/app/app.constants';
import { CommonService } from 'src/app/services/common/common.service';
import { GTMSegmentService } from 'src/app/services/gtm-segment.service';
import { JackpotTriggersService } from 'src/app/services/jackpot-triggers.service';
import { LuckyNumberService } from 'src/app/services/lucky-number.service';
import { MixpanelService } from 'src/app/services/mixpanel.service';
import { SyndicateDataService } from 'src/app/services/syndicate-data.service';
import { SyndicatePaymentService } from 'src/app/services/SyndicateService/syndicate-payment.service';
import { StoreRepositoryService } from '../store/store-repository.service';
import { SyndicateRepositoryService } from './syndicate-repository.service';

// State interfaces
export interface GoldRingState {
  details: Data;
  remaining: number;
  used: number;
  icon_url?: string;
  title?: string;
  subtitle?: string;
}

export interface LuckyNumberState {
  details: Data;
  remaining: number;
  used: number;
  title?: string;
  subtitle?: string;
}

export interface JackpotState {
  triggers: Data[];
  activeCount: number;
}

export interface PreviousCheckState {
  stats: Data;
  message: Data;
  showAnimation: boolean;
  showCouch: boolean;
}

export interface BannerState {
  status: boolean;
}

// UI State interfaces for widget visibility
export interface GoldRingUIState {
  showMiniWidget: boolean;
  showExpandWidget: boolean;
  showAvailableWidget: boolean;
  showToolTip: boolean;
}

@Injectable({
  providedIn: null
})
export class SyndicateWidgetsFacadeService implements OnDestroy {

  // Observable state - page subscribes and binds to template
  readonly goldRingData$ = new BehaviorSubject<GoldRingState>({
    details: {},
    remaining: 0,
    used: 0
  });

  readonly luckyNumberData$ = new BehaviorSubject<LuckyNumberState>({
    details: {},
    remaining: 0,
    used: 0
  });

  readonly jackpotData$ = new BehaviorSubject<JackpotState>({
    triggers: [],
    activeCount: 0
  });

  readonly previousChecks$ = new BehaviorSubject<PreviousCheckState>({
    stats: null,
    message: null,
    showAnimation: false,
    showCouch: false
  });

  readonly bannerStatus$ = new BehaviorSubject<BannerState>({
    status: false
  });

  // Internal state for direct access
  private _goldRingDetails: Data = {};
  private _luckyNumberDetails: Data = {};
  private _jackpotTriggers: Data[] = [];
  private _previousCheckStats: Data = null;
  private _previousCheckMessage: Data = null;
  private _showPreviousCheckAnimation = false;
  private _showPreviousCheckCouch = false;

  private destroy$ = new Subject<void>();
  private firestoreUnsubscribers: (() => void)[] = [];

  private userId: string;
  private languageCode: string = CommonVariable.preferredLanguage;

  constructor(
    private firestore: Firestore,
    private ngZone: NgZone,
    private authService: AuthService,
    private luckyNumberService: LuckyNumberService,
    private jackpotTriggerService: JackpotTriggersService,
    private syndicateDataService: SyndicateDataService,
    private commonService?: CommonService,
    private gtmSegmentService?: GTMSegmentService,
    private mixpanelService?: MixpanelService,
    private syndicatePaymentService?: SyndicatePaymentService,
    private storeService?: StoreRepositoryService,
    private syndicateService?: SyndicateRepositoryService,
    private authGamesService?: AuthGamesService
  ) { }

  /**
   * Called once from page ngOnInit
   */
  initialise(userId: string, languageCode: string): void {
    this.teardown();
    this.userId = userId;
    this.languageCode = languageCode || CommonVariable.preferredLanguage;

    // Wait for any in-progress token refresh to finish before opening Firestore
    // listeners.  tokenRefreshInProgress$ is false for established sessions, so
    // this emits immediately and adds no perceptible latency in the normal case.
    this.authService.tokenRefreshInProgress$.pipe(
      filter(inProgress => !inProgress),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.setupJackpotListeners(this.userId, this.languageCode);
      this.setupGoldRingWidget(this.userId);
      this.setupLuckyNumberWidget(this.userId);
      this.setupPreviousChecksWidget(this.userId);
      this.setupBannerListener(this.userId);
    });
  }


  /**
   * Unsubscribe all Firestore listeners with error suppression
   */
  private unsubscribeFirestoreListeners(): void {
    this.firestoreUnsubscribers.forEach(fn => {
      try {
        if (typeof fn === 'function') {
          fn();
        }
      } catch (error) {
        // Silently suppress unsubscription errors to prevent UnsubscriptionError
        // These errors occur when nested listeners clean up state during destruction
      }
    });
    this.firestoreUnsubscribers = [];
  }

  /**
   * Setup jackpot triggers listeners with nested onSnapshot
   */
  private setupJackpotListeners(userId: string, languageCode: string): void {
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    // Subscribe to syndicate jackpot triggers from service
    this.syndicateDataService?.getSyndicateJackpotTriggers(userId).pipe(
      takeUntil(this.destroy$),
      throttleTime(300),
      catchError(() => of([]))
    ).subscribe((triggers) => {
      this.handleJackpotTriggers(triggers);
    });
  }

  /**
   * Handle jackpot triggers and set up nested onSnapshot listeners
   */
  private handleJackpotTriggers(triggers: any): void {
    if (!triggers || !Array.isArray(triggers)) {
      return;
    }

    const jackpotTriggers: any[] = triggers;

    // Clear old listeners first
    this.firestoreUnsubscribers.forEach(fn => {
      try {
        if (typeof fn === 'function') {
          fn();
        }
      } catch {
        // Suppress errors during cleanup
      }
    });
    this.firestoreUnsubscribers = [];

    triggers.forEach((syndicateTrigger: any) => {
      if (!syndicateTrigger) return;

      const currentJackpotRef = syndicateTrigger?.current_jackpot;
      const triggerRef = syndicateTrigger?.trigger;

      if (!currentJackpotRef) {
        return;
      }

      // Store unsubscribe function for cleanup - wrap in ngZone.runOutsideAngular to prevent change detection conflicts
      this.ngZone.runOutsideAngular(() => {
        const unsubJackpot = onSnapshot(
          currentJackpotRef,
          (jackpotSnap) => {
            this.ngZone.run(() => {
              syndicateTrigger["jackpotData"] = jackpotSnap.data();
              this.updateJackpotState(triggers);
            });
          },
          (error) => {
            // Silently handle Firestore errors to prevent unsubscription issues
          }
        );
        this.firestoreUnsubscribers.push(unsubJackpot);
      });

      if (!triggerRef) {
        return;
      }

      // Store unsubscribe function for cleanup
      this.ngZone.runOutsideAngular(() => {
        const unsubTrigger = onSnapshot(
          triggerRef,
          (triggerSnap) => {
            const triggerData = triggerSnap.data();
            syndicateTrigger["triggerData"] = triggerData;

            const lotteryRef = triggerData?.lottery;
            if (!lotteryRef) {
              return;
            }

            // Store unsubscribe function for cleanup
            this.ngZone.runOutsideAngular(() => {
              const unsubLottery = onSnapshot(
                lotteryRef,
                (lotterySnap) => {
                  this.ngZone.run(() => {
                    if (syndicateTrigger["triggerData"]) {
                      syndicateTrigger["triggerData"]["lotteryDetails"] = lotterySnap.data();
                    }

                    try {
                      this.updateJackpotState(triggers);
                    } catch (e) {
                      // Log the error but prevent crash
                    }
                  });
                },
                (error) => {
                  // Silently handle Firestore errors
                }
              );
              this.firestoreUnsubscribers.push(unsubLottery);
            });
          },
          (error) => {
            // Silently handle Firestore errors to prevent unsubscription issues
          }
        );
        this.firestoreUnsubscribers.push(unsubTrigger);
      });
    });
  }

  /**
   * Update jackpot state
   */
  private updateJackpotState(triggers: any[]): void {
    this._jackpotTriggers = triggers;
    const activeCount = triggers?.filter(t => t?.triggerData?.active)?.length || 0;
    this.jackpotData$.next({
      triggers: triggers,
      activeCount: activeCount
    });
  }

  /**
   * Setup banner status listener
   */
  private setupBannerListener(userId: string): void {
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    const docRef = doc(this.firestore, `users/${userId}/main_pages/syndicate`);

    this.ngZone.runOutsideAngular(() => {
      const unsubscribe = onSnapshot(
        docRef,
        (docSnap) => {
          this.ngZone.run(() => {
            try {
              const data = docSnap?.data();
              const bannerStatus = data?.syndicate_banner?.banner_status;
              const expiryDate = data?.syndicate_banner?.banner_expiry?.seconds * 1000;
              const isNotExpired = expiryDate > Date.now();

              this.bannerStatus$.next({
                status: bannerStatus && isNotExpired
              });
            } catch (e) {
              // Suppress errors during state update
            }
          });
        },
        (error) => {
          this.ngZone.run(() => {
            if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
              this.bannerStatus$.next({ status: false });
            }
          });
        }
      );

      if (typeof unsubscribe === 'function') {
        this.firestoreUnsubscribers.push(unsubscribe);
      }
    });
  }

  /**
   * Setup gold ring widget with Firestore listener
   */
  private setupGoldRingWidget(userId: string): void {
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    const widgetDocRef = doc(this.firestore, `users/${userId}/extras/widgets`);
    const balanceDocRef = doc(this.firestore, `users/${userId}/main_pages/syndicate`);
    this.fetchGoldRingBalanceAndUpdateState(userId);

    this.ngZone.runOutsideAngular(() => {
      const unsubscribeWidgets = onSnapshot(
        widgetDocRef,
        (snapshot) => {
          this.ngZone.run(() => {
            try {
              const data = snapshot.data();
              // Get gold_rings from extras/widgets path
              const goldRingWidget = data?.gold_rings ?? {};

              this._goldRingDetails = goldRingWidget && typeof goldRingWidget === 'object'
                ? goldRingWidget
                : {};

              this.fetchGoldRingBalanceAndUpdateState(userId);
            } catch (e) {
              // Suppress errors during state update
            }
          });
        },
        (error) => {
          this.ngZone.run(() => {
            if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
              this.goldRingData$.next({
                details: {},
                remaining: 0,
                used: 0
              });
            }
          });
        }
      );
      if (typeof unsubscribeWidgets === 'function') {
        this.firestoreUnsubscribers.push(unsubscribeWidgets);
      }

      const unsubscribeBalance = onSnapshot(
        balanceDocRef,
        (snapshot) => {
          this.ngZone.run(() => {
            try {
              this.fetchGoldRingBalanceAndUpdateState(userId);
            } catch (e) {
              // Suppress errors during state update
            }
          });
        },
        (error) => {
          this.ngZone.run(() => {
            if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
              // Handle permission error
            }
          });
        }
      );
      if (typeof unsubscribeBalance === 'function') {
        this.firestoreUnsubscribers.push(unsubscribeBalance);
      }
    });
  }


  private fetchGoldRingBalanceAndUpdateState(userId: string): void {
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    const balanceDocRef = doc(this.firestore, `users/${userId}/main_pages/syndicate`);

    // Get balance data
    this.luckyNumberService?.getUserCurrentBalance()?.pipe(
      takeUntil(this.destroy$),
      catchError(() => of(null))
    ).subscribe((balance: Data) => {
      const remaining = balance?.gold_rings?.remaining ?? 0;
      const used = balance?.gold_rings?.in_use ?? 0;

      const fullDetails = {
        ...this._goldRingDetails,

        background_color: this._goldRingDetails?.background_color ?? "#ffffff",
        button_label: this._goldRingDetails?.button_label ?? "Show",
        button_link: this._goldRingDetails?.button_link ?? "/syndicate?highlight=gold_rings",
        icon: this._goldRingDetails?.icon ?? "gold_rings.png",
        icon_url: this._goldRingDetails?.icon_url ?? "assets/images/gold_ring.svg",
        remaining: remaining,
        used: used,
        subtitle: remaining === 0
          ? (this._goldRingDetails?.all_in_use ?? "All in use")
          : remaining > 0
            ? (this._goldRingDetails?.remaining ?? `${remaining} Remaining`)
            : undefined,
        title: this._goldRingDetails?.title ?? "Gold Rings"
      };

      const state: GoldRingState = {
        details: fullDetails,
        remaining,
        used,
        icon_url: fullDetails.icon_url,
        title: fullDetails.title,
        subtitle: fullDetails.subtitle
      };

      this.goldRingData$.next(state);
    });
  }

  /**
   * Setup lucky number widget with Firestore listener
   */
  private setupLuckyNumberWidget(userId: string): void {
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    const syndicateDocRef = doc(this.firestore, `users/${userId}/main_pages/syndicate`);

    this.ngZone.runOutsideAngular(() => {
      const unsubscribe = onSnapshot(
        syndicateDocRef,
        (snapshot) => {
          this.ngZone.run(() => {
            try {
              const data = snapshot.data();
              const luckyNumbers = data?.lucky_numbers ?? {};

              this._luckyNumberDetails = luckyNumbers && typeof luckyNumbers === 'object'
                ? luckyNumbers
                : {};

              const remainingBalance = parseInt(this._luckyNumberDetails?.remaining) || 0;
              const usedBalance = parseInt(this._luckyNumberDetails?.used) || 0;

              const state: LuckyNumberState = {
                details: this._luckyNumberDetails,
                remaining: remainingBalance,
                used: usedBalance,
                title: this._luckyNumberDetails?.title,
                subtitle: remainingBalance === 0
                  ? this._luckyNumberDetails?.all_in_use
                  : remainingBalance > 0
                    ? this._luckyNumberDetails?.remaining
                    : undefined
              };

              this.luckyNumberData$.next(state);
            } catch (e) {
              // Suppress errors during state update
            }
          });
        },
        (error) => {
          this.ngZone.run(() => {
            if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
              this.luckyNumberData$.next({
                details: {},
                remaining: 0,
                used: 0
              });
            }
          });
        }
      );

      if (typeof unsubscribe === 'function') {
        this.firestoreUnsubscribers.push(unsubscribe);
      }
    });
  }

  /**
   * Setup previous checks widget
   */
  private setupPreviousChecksWidget(userId: string): void {
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    // Get check winnings stats
    const statsDocRef = doc(this.firestore, `users/${userId}/check_winnings_stats/current`);

    this.ngZone.runOutsideAngular(() => {
      const unsubscribeStats = onSnapshot(
        statsDocRef,
        (snapshot) => {
          this.ngZone.run(() => {
            try {
              this._previousCheckStats = snapshot.data();
              this.updatePreviousCheckState();
            } catch (e) {
              // Suppress errors during state update
            }
          });
        },
        () => {
          this.ngZone.run(() => {
            this._previousCheckStats = null;
            this.updatePreviousCheckState();
          });
        }
      );
      if (typeof unsubscribeStats === 'function') {
        this.firestoreUnsubscribers.push(unsubscribeStats);
      }
    });

    // Get stats message
    const msgDocRef = doc(this.firestore, `general_modules/check_winnings_stats_message`);

    this.ngZone.runOutsideAngular(() => {
      const unsubscribeMsg = onSnapshot(
        msgDocRef,
        (snapshot) => {
          this.ngZone.run(() => {
            try {
              this._previousCheckMessage = snapshot.data();
              this.updatePreviousCheckState();
            } catch (e) {
              // Suppress errors during state update
            }
          });
        },
        () => {
          this.ngZone.run(() => {
            this._previousCheckMessage = null;
            this.updatePreviousCheckState();
          });
        }
      );
      if (typeof unsubscribeMsg === 'function') {
        this.firestoreUnsubscribers.push(unsubscribeMsg);
      }
    });

    // Get one-time features
    const featuresDocRef = doc(collection(doc(this.firestore, `users/${userId}`), 'special_features'), 'enable_features_one_time');

    this.ngZone.runOutsideAngular(() => {
      const unsubscribeFeatures = onSnapshot(
        featuresDocRef,
        (snapshot) => {
          this.ngZone.run(() => {
            try {
              const data = snapshot.data();
              this._showPreviousCheckAnimation = data?.previous_check_animation === true;
              this._showPreviousCheckCouch = data?.previous_widget_couch === true;
              this.updatePreviousCheckState();
            } catch (e) {
              // Suppress errors during state update
            }
          });
        },
        () => {
          this.ngZone.run(() => {
            this._showPreviousCheckAnimation = false;
            this._showPreviousCheckCouch = false;
            this.updatePreviousCheckState();
          });
        }
      );
      if (typeof unsubscribeFeatures === 'function') {
        this.firestoreUnsubscribers.push(unsubscribeFeatures);
      }
    });
  }

  /**
   * Update previous check state observable
   */
  private updatePreviousCheckState(): void {
    const state: PreviousCheckState = {
      stats: this._previousCheckStats,
      message: this._previousCheckMessage,
      showAnimation: this._showPreviousCheckAnimation,
      showCouch: this._showPreviousCheckCouch
    };
    this.previousChecks$.next(state);
  }

  /**
   * Update previous check couch (called from page)
   */
  updatePreviousCheckCouch(value: boolean): void {
    this._showPreviousCheckCouch = value;
    this.updatePreviousCheckState();
  }

  /**
   * Get current gold ring details (for backward compatibility)
   */
  getGoldRingDetails(): Data {
    return this._goldRingDetails;
  }

  /**
   * Get current lucky number details (for backward compatibility)
   */
  getLuckyNumberDetails(): Data {
    return this._luckyNumberDetails;
  }

  /**
   * Get jackpot triggers (for backward compatibility)
   */
  getJackpotTriggers(): Data[] {
    return this._jackpotTriggers;
  }

  /**
   * Get previous check stats (for backward compatibility)
   */
  getPreviousCheckStats(): Data {
    return this._previousCheckStats;
  }

  /**
   * Get lucky number data observable (for page subscription)
   */
  getLuckyNumberData(): Observable<any> {
    return this.luckyNumberData$.asObservable();
  }

  /**
   * Get gold ring data observable (for page subscription)
   */
  getGoldRingData(): Observable<any> {
    return this.goldRingData$.asObservable();
  }

  /**
   * Show gold rings mini widget (legacy method for
   */
  async showGoldRingsMiniWidget(
    userId: string,
    changeDetectorRef: ChangeDetectorRef,
    widgetFirestoreUnsubscribers: Array<() => void>,
    onDataLoaded?: (goldRingWidget: Data) => void
  ): Promise<void> {
    // Validate userId
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    // Get current gold ring data
    const currentState = this.goldRingData$.getValue();
    const hasVisited = localStorage.getItem("hasVisitedGR") ?? false;
    const remaining = currentState?.remaining ?? 0;

    // Call the callback with current data
    if (onDataLoaded) {
      onDataLoaded(currentState?.details || this._goldRingDetails);
    }
    if (remaining && !hasVisited) {

      this._goldRingUIState.showToolTip = true;
    }

    if (changeDetectorRef) {
      changeDetectorRef.markForCheck();
    }
  }

  // Internal UI state for gold ring widget
  private _goldRingUIState: GoldRingUIState = {
    showMiniWidget: true,
    showExpandWidget: false,
    showAvailableWidget: false,
    showToolTip: false
  };


  getGoldRingUIState(): Observable<GoldRingUIState> {
    return of(this._goldRingUIState);
  }


  enableGoldRingMiniOnly(): void {
    this._goldRingUIState = {
      ...this._goldRingUIState,
      showMiniWidget: true,
      showExpandWidget: false,
      showAvailableWidget: false
    };
  }


  checkAndSetupGoldRingToolTip(): boolean {
    const hasVisited = localStorage.getItem("hasVisitedGR") ?? false;
    const remaining = this.goldRingData$.getValue()?.remaining ?? 0;

    if (!hasVisited && remaining) {
      this._goldRingUIState.showToolTip = true;
      localStorage.setItem("hasVisitedGR", "true");
      return true;
    }

    this._goldRingUIState.showToolTip = false;
    return false;
  }


  dismissGoldRingToolTip(): void {
    this._goldRingUIState.showToolTip = false;
    localStorage.setItem("hasVisitedGR", "true");
  }

  /**
   * Show mini widgets - enables mini widget view
   */

  /**
   * Get gold rings data from Firestore
   */
  getGoldRingsData(userId: string): void {
    if (!userId || userId === 'null' || userId.length < 5) {
      return;
    }

    const goldRingsDocRef = doc(this.firestore, `users/${userId}/main_pages/syndicate`);

    this.ngZone.runOutsideAngular(() => {
      const unsubscribe = onSnapshot(
        goldRingsDocRef,
        (snapshot) => {
          this.ngZone.run(() => {
            const data = snapshot.data();
            const goldRingData = data?.gold_rings ?? {};

            this.luckyNumberService?.getUserCurrentBalance()?.pipe(
              takeUntil(this.destroy$),
              catchError(() => of(null))
            ).subscribe((balance: any) => {
              const remaining = balance?.gold_rings?.remaining ?? 0;
              const used = balance?.gold_rings?.in_use ?? 0;

              const state: GoldRingState = {
                details: goldRingData,
                remaining,
                used,
                icon_url: goldRingData?.icon_url,
                title: goldRingData?.title,
                subtitle: remaining === 0
                  ? goldRingData?.all_in_use
                  : remaining > 0
                    ? goldRingData?.remaining
                    : undefined
              };

              this.goldRingData$.next(state);
            });
          });
        },
        (error) => {
          this.ngZone.run(() => {
            if (error?.code === 'permission-denied') {
              this.goldRingData$.next({
                details: {},
                remaining: 0,
                used: 0
              });
            }
          });
        }
      );

      this.firestoreUnsubscribers.push(unsubscribe);
    });
  }

  /**
   * Get tooltip content from Firestore
   */
  getToolTipContent(
    languageCode: string
  ): Observable<{ toolTipMsg: string; goldRingToolTipMsg: string }> {

    const luckyNumber$ = this.authService
      .getAngularFireStore(`/Global_lookup/onboarding/tooltips/lucky-number-mini`, "get")
      ?.pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      ) ?? of(null);

    const goldRing$ = this.authService
      .getAngularFireStore(`/Global_lookup/onboarding/tooltips/gold-rings-mini`, "get")
      ?.pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      ) ?? of(null);

    return combineLatest([luckyNumber$, goldRing$]).pipe(
      take(1),
      map(([toolTipRef, goldRingToolTipRef]: any) => {

        const toolTipMsg =
          toolTipRef?.data()?._title?.[languageCode] ||
          toolTipRef?.data()?.title ||
          '';

        const goldRingToolTipMsg =
          goldRingToolTipRef?.data()?.content?.[languageCode]?.title ||
          goldRingToolTipRef?.data()?.title ||
          '';

        return { toolTipMsg, goldRingToolTipMsg };
      })
    );
  }

  /**
   * Get store and syndicate details
   */
  getStoreAndSyndicateDetails(): Observable<{ storeDiscountDetails: any; syndicateID: string; syndicatePaymentStatus: string; isShown: boolean }> {
    return combineLatest([
      this.storeService?.storeOffers$ || of(null),
      this.syndicateService?.getSyndicateCustomerPurchaseDetails$ || of(null),
    ]).pipe(
      takeUntil(this.destroy$),
      catchError(() => of([null, null])),
      map(([storeDetails, syndicateDetails]: [any, any]) => {
        const storeDiscountDetails = storeDetails?.cash_offer_discount?.[0];

        let syndicateID = '';
        let syndicatePaymentStatus = '';
        let isShown = false;

        try {
          syndicateID = syndicateDetails?.id;
          const processedStatus = syndicateDetails?.processed_status;
          isShown = syndicateDetails?.isShown;

          if (processedStatus === -1 && isShown === undefined) {
            syndicatePaymentStatus = "failed";
            isShown = false;
          } else if (processedStatus === 1 && isShown === undefined) {
            syndicatePaymentStatus = "pending";
            isShown = false;
          } else if (processedStatus === 2 && isShown === undefined) {
            syndicatePaymentStatus = "success";
            isShown = false;
          }
        } catch (error) {
          // Handle error
        }

        return { storeDiscountDetails, syndicateID, syndicatePaymentStatus, isShown };
      })
    );
  }

  teardown(): void {
    // Phase 1: Signal existing RxJS takeUntil subscriptions to stop
    // This prevents in-flight operations from attempting to update state
    this.destroy$.next();
    this.destroy$.complete();
    // Phase 2: Recreate destroy$ synchronously so it is ready for the next
    // initialise() cycle.  Must happen before any new pipe(takeUntil(destroy$))
    // subscriptions are created, otherwise they would immediately complete on a
    // closed Subject.
    this.destroy$ = new Subject<void>();
    // Phase 3: Unsubscribe Firestore listeners with a short delay to let any
    // in-flight onSnapshot callbacks finish cleanly before tearing them down.
    setTimeout(() => {
      this.unsubscribeFirestoreListeners();
      // Re-create destroy$ for the next cycle (in case service is reused)
    }, 50);
  }
  ngOnDestroy(): void {
    this.teardown();
  }
}

